import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Activity, MapPin } from 'lucide-react';

const FitBounds = ({ devices }) => {
    const map = useMap();
    useEffect(() => {
        if (!devices || devices.length === 0) return;
        
        // Filter out devices without valid coordinates
        const validDevices = devices.filter(d => d.latitude != null && d.longitude != null);
        if (validDevices.length === 0) return;

        const bounds = L.latLngBounds(validDevices.map(d => [d.latitude, d.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
    }, [devices, map]);
    return null;
};

// Create a custom SVG Icon for the map markers to stand out against realistic satellite maps
const createCustomIcon = (bgColorHex = '#C47A2C') => {
    const svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${bgColorHex}" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8 drop-shadow-md">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3" fill="white"></circle>
        </svg>
    `;

    return L.divIcon({
        className: 'bg-transparent border-none', // Strips leaflet's default heavy square box rendering
        html: svgIcon,
        iconSize: [32, 32],
        iconAnchor: [16, 32], // Anchor to the bottom tip
        tooltipAnchor: [16, -20] // Tooltip stems from slightly above the icon
    });
};

const IoTMap = ({ devices, theme, t }) => {
    // Determine dynamic background styles for loading states
    const isDark = theme === 'dark';

    if (!devices || devices.length === 0) {
        return (
            <div className="col-span-12 mt-6 p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-[#1B263B]/5 dark:border-slate-700 flex flex-col items-center justify-center transition-colors h-[400px]">
                <MapPin className="text-gray-300 dark:text-slate-600 mb-4" size={48} />
                <p className="text-gray-500 dark:text-gray-400 font-medium">{t ? t.iot.noDeviceLocation : 'No active device location coordinates available'}</p>
            </div>
        );
    }

    return (
        <div className="col-span-12 mt-8">
            <h3 className="text-xl font-bold text-[#1B263B] dark:text-white mb-6 flex items-center gap-2">
                <MapPin className="text-[#6B8F71] animate-smooth-float" />
                {t ? t.iot.satelliteMatrix : 'Satellite Device Matrix'}
            </h3>
            
            <div className="w-full h-[450px] rounded-2xl overflow-hidden shadow-md border border-[#1B263B]/10 dark:border-slate-700 relative z-0 transition-colors">
                <MapContainer center={[0, 0]} zoom={2} className="w-full h-full" zoomControl={true}>
                    {/* ESRI World Imagery - High Resolution Realistic Satellite Tiles */}
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                    />
                    
                    <FitBounds devices={devices} />

                    {devices.map((device, idx) => {
                        if (device.latitude == null || device.longitude == null) return null;
                        
                        // Alternate colors based on odd/even or device properties just to differentiate
                        const iconColor = idx % 2 === 0 ? '#C47A2C' : '#6B8F71'; 
                        
                        return (
                            <Marker 
                                key={`map-device-${device.id}`} 
                                position={[device.latitude, device.longitude]} 
                                icon={createCustomIcon(iconColor)}
                            >
                                <Tooltip permanent direction="top" offset={[0, 0]} className={`font-bold custom-satellite-tooltip ${isDark ? 'dark-map-label' : 'light-map-label'}`}>
                                    {device.device_name || `Sensor Node ${device.id}`}
                                </Tooltip>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>
        </div>
    );
};

export default IoTMap;
